import { CustomTag } from "sipli-fleet-core/components/CustomSharedTag";
import { App, Dropdown } from "antd";
import type { TableProps } from "antd";
import { useCallback, useMemo, useState, type ComponentType, type MouseEvent } from "react";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import {
  CustomButton,
  CustomTable,
  CustomTabs,
  CustomTypography,
} from "sipli-fleet-core/components";
import CustomSharedConfirmationModal from "sipli-fleet-core/components/CustomSharedConfirmationModal/CustomSharedConfirmationModal";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import InstallUnitModal from "src/container/DeviceManagement/Install/InstallUnitModal";
import AddUnitModal from "src/container/WarehouseManagement/AllUnits/AddUnitModal";
import EditUnitModal from "src/container/WarehouseManagement/AllUnits/EditUnitModal";
import EditUnitPositionModal from "src/container/WarehouseManagement/AllUnits/EditUnitPositionModal";
import {
  DELIVERED_ICON,
  MOVED_ICON,
  READY_TO_INSTALL_ICON,
  RECEIVED_ICON,
  RETURNED_ICON,
} from "src/assets/svgs";
import {
  useMutationDeleteWarehouseUnit,
  useMutationUpdateWarehouseUnitOperationType,
  useQueryGetWarehouseUnitOperationTypeLookup,
  useQueryGetWarehouseUnitsList,
} from "src/queries/warehouseUnits.queries";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import type {
  WarehouseUnitListParams,
  WarehouseUnitNestedEntity,
  WarehouseUnitRecord,
  WarehouseUnitType,
} from "src/services/warehouseUnitService";
import "./AllUnits.css";

const { CustomText } = CustomTypography;
type WarehouseUnitColumns = NonNullable<TableProps<WarehouseUnitRecord>["columns"]>;
type WarehouseUnitColumn = WarehouseUnitColumns[number];
type WarehouseUnitSorting = {
  sortBy: string | undefined;
  sortDir: "ASC" | "DESC" | undefined;
};

const DEFAULT_SORTING: WarehouseUnitSorting = {
  sortBy: "ID",
  sortDir: "DESC",
};

const warehouseUnitDataIndexToColumnId: Record<string, string> = {
  phoneNumber: "PHONE_NUMBER",
  configurationStatus: "IS_ACTIVE",
  hwStatus: "UNIT_STATUS",
  operationType: "OPERATION_TYPE",
};

const DeliveredIcon = DELIVERED_ICON as unknown as ComponentType<{
  className?: string;
}>;

const MovedIcon = MOVED_ICON as unknown as ComponentType<{
  className?: string;
}>;

const ReadyToInstallIcon = READY_TO_INSTALL_ICON as unknown as ComponentType<{
  className?: string;
}>;

const ReceivedIcon = RECEIVED_ICON as unknown as ComponentType<{
  className?: string;
}>;

const ReturnedIcon = RETURNED_ICON as unknown as ComponentType<{
  className?: string;
}>;

const DEFAULT_PAGINATION = {
  page: 0,
  size: 10,
};

const UNIT_TABS: Array<{ key: WarehouseUnitType; labelKey: string }> = [
  { key: "DVC", labelKey: "devices" },
  { key: "FUESNSR", labelKey: "fuel_sensors" },
];

const getOptionalDistributorId = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const getEntityName = (value: unknown) => {
  if (typeof value === "string") return value;

  if (value && typeof value === "object") {
    const candidate = value as Record<string, unknown>;
    return String(
      candidate.name ?? candidate.code ?? candidate.enumName ?? candidate.description ?? ""
    );
  }

  return "";
};

const getUnitTitle = (record: WarehouseUnitRecord) => {
  const makeName = getEntityName(record.make);
  const modelName = getEntityName(record.model);
  const explicitName = String(record.name ?? "").trim();
  return [makeName, modelName].filter(Boolean).join(" ") || explicitName || "--";
};

const getConfigurationActive = (record: WarehouseUnitRecord) => {
  if (
    record.active &&
    typeof record.active === "object" &&
    typeof record.active.id === "boolean"
  ) {
    return record.active.id;
  }

  const statusValue = getEntityName(record.configurationStatus || record.status);
  return statusValue.toLowerCase() === "active";
};

const getConfigurationLabel = (record: WarehouseUnitRecord, t: (key: string) => string) => {
  const statusValue = getEntityName(record.configurationStatus || record.status);

  if (statusValue) return statusValue;
  return getConfigurationActive(record) ? t("active") : t("inactive");
};

const normalizeVariantKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[\s-_]+/g, "_");

const getHardwareBadgeVariant = (value: string) => {
  const normalized = normalizeVariantKey(value);

  if (normalized === "new") {
    return "bg-primary6 text-primary";
  }

  if (normalized === "broken") {
    return "bg-red-soft text-red";
  }

  if (normalized === "repaired") {
    return "bg-status-work-bg text-purple";
  }

  return "bg-gray1 text-gray9";
};

const getOperationBadgeVariant = (value: string) => {
  const normalized = normalizeVariantKey(value);

  if (normalized === "received") {
    return "text-blue5";
  }

  if (normalized === "ready_to_install") {
    return "text-green2";
  }

  if (normalized === "uninstalled") {
    return "text-gray6";
  }

  if (normalized === "internal_transfer") {
    return "text-gray6";
  }

  if (normalized === "delivered") {
    return "text-gray8";
  }

  return "text-gray6";
};

const getOperationIcon = (value: string) => {
  const normalized = normalizeVariantKey(value);

  if (normalized === "received") return <ReceivedIcon className="all-units-operation-icon" />;
  if (normalized === "ready_to_install")
    return <ReadyToInstallIcon className="all-units-operation-icon" />;
  if (normalized === "uninstalled") return <ReturnedIcon className="all-units-operation-icon" />;
  if (normalized === "internal_transfer") return <MovedIcon className="all-units-operation-icon" />;
  if (normalized === "delivered") return <DeliveredIcon className="all-units-operation-icon" />;

  return null;
};

const HW_STATUS_TRANSLATION_KEYS: Record<string, string> = {
  new: "hw_status_new",
  broken: "hw_status_broken",
  repaired: "hw_status_repaired",
};

const OPERATION_TYPE_TRANSLATION_KEYS: Record<string, string> = {
  received: "operation_type_received",
  ready_to_install: "operation_type_ready_to_install",
  uninstalled: "operation_type_uninstalled",
  internal_transfer: "operation_type_internal_transfer",
  delivered: "operation_type_delivered",
};

const translateBadgeValue = (
  value: string,
  t: (key: string) => string,
  translationKeys: Record<string, string>
) => {
  const translationKey = translationKeys[normalizeVariantKey(value)];
  return translationKey ? t(translationKey) : value;
};

const isReadyToInstallOperation = (record: WarehouseUnitRecord) =>
  normalizeVariantKey(getEntityName(record.operationType)) === "ready_to_install";

const getActionLabel = (record: WarehouseUnitRecord, t: (key: string) => string) =>
  isReadyToInstallOperation(record) ? t("install") : t("set_as_ready_to_install");

const isReadyToInstallOperationTypeEntry = (entry: WarehouseUnitNestedEntity) => {
  if (normalizeVariantKey(getEntityName(entry)) === "ready_to_install") return true;
  const code = String(entry.code ?? entry.enumName ?? "").toUpperCase();
  return code === "RTINST";
};

const UnitInfoCell = ({
  record,
  serialOnly = false,
}: {
  record: WarehouseUnitRecord;
  serialOnly?: boolean;
}) => (
  <div className="min-w-55">
    <CustomText className="text-sm! font-bold! text-black!">{getUnitTitle(record)}</CustomText>
    <div className="mt-2 space-y-1 text-sm text-gray8">
      <div>Serial: {record.serial || "--"}</div>
      {!serialOnly && (
        <>
          <div>IMEI: {record.imei || "--"}</div>
          <div>ICCID: {record.iccid || "--"}</div>
        </>
      )}
    </div>
  </div>
);

const PositionCell = ({
  record,
  t,
}: {
  record: WarehouseUnitRecord;
  t: (key: string) => string;
}) => (
  <div className="min-w-60 space-y-1 text-xs font-medium leading-4.5 text-gray9">
    <div>
      {t("warehouse_name")}: {getEntityName(record.warehouse) || "--"}
    </div>
    <div>
      {t("area_name")}: {getEntityName(record.area) || "--"}
    </div>
    <div>
      {t("lane_number")}: {getEntityName(record.lane) || "--"}
    </div>
    <div>
      {t("shelf_number")}: {getEntityName(record.shelf) || "--"}
    </div>
    <div>
      {t("position")}: {getEntityName(record.position) || "--"}
    </div>
  </div>
);

const getBaseColumns = ({
  t,
  handleEditUnit,
  handleEditUnitPosition,
  setDeleteRecord,
  handleSetReadyToInstall,
  handleInstallUnit,
  readyToInstallPendingId,
  serialOnly = false,
  sorting,
}: {
  t: (key: string) => string;
  handleEditUnit: (record: WarehouseUnitRecord) => void;
  handleEditUnitPosition: (record: WarehouseUnitRecord) => void;
  setDeleteRecord: (record: WarehouseUnitRecord | null) => void;
  handleSetReadyToInstall: (record: WarehouseUnitRecord) => void;
  handleInstallUnit: (record: WarehouseUnitRecord) => void;
  readyToInstallPendingId: WarehouseUnitRecord["id"] | null;
  serialOnly?: boolean;
  sorting: WarehouseUnitSorting;
}): WarehouseUnitColumns => [
  {
    title: t("unit"),
    key: "unit",
    render: (_, record) => <UnitInfoCell record={record} serialOnly={serialOnly} />,
  },
  {
    title: t("position"),
    key: "position",
    render: (_, record) => <PositionCell record={record} t={t} />,
  },
  {
    title: t("hw_status"),
    dataIndex: "hwStatus",
    key: "hwStatus",
    sorter: true,
    sortOrder:
      sorting.sortBy === "UNIT_STATUS" ? (sorting.sortDir === "ASC" ? "ascend" : "descend") : null,
    render: (_, record) => {
      const value =
        getEntityName(record.hardwareStatus || record.hwStatus || record.unitStatus) || "--";

      return (
        <div className="pt-7">
          <CustomTag
            className={`justify-center rounded-xs px-2 py-1 text-sm font-normal ${getHardwareBadgeVariant(
              value
            )}`}
          >
            {translateBadgeValue(value, t, HW_STATUS_TRANSLATION_KEYS)}
          </CustomTag>
        </div>
      );
    },
  },
  {
    title: t("operation_type"),
    dataIndex: "operationType",
    key: "operationType",
    sorter: true,
    sortOrder:
      sorting.sortBy === "OPERATION_TYPE"
        ? sorting.sortDir === "ASC"
          ? "ascend"
          : "descend"
        : null,
    render: (_, record) => {
      const value = getEntityName(record.operationType) || "--";

      return (
        <div className="pt-7">
          <CustomTag
            className="justify-center gap-1.5 bg-gray1 text-gray10"
            icon={<span className={getOperationBadgeVariant(value)}>{getOperationIcon(value)}</span>}
          >
            {translateBadgeValue(value, t, OPERATION_TYPE_TRANSLATION_KEYS)}
          </CustomTag>
        </div>
      );
    },
  },
  {
    title: t("action"),
    key: "action",
    render: (_, record) => (
      <UnitActionCell
        record={record}
        t={t}
        handleEditUnit={handleEditUnit}
        handleEditUnitPosition={handleEditUnitPosition}
        setDeleteRecord={setDeleteRecord}
        handleSetReadyToInstall={handleSetReadyToInstall}
        handleInstallUnit={handleInstallUnit}
        readyToInstallPendingId={readyToInstallPendingId}
      />
    ),
  },
];

const getConfigurationStatusColumn = ({
  t,
  sorting,
}: {
  t: (key: string) => string;
  sorting: WarehouseUnitSorting;
}): WarehouseUnitColumn => ({
  title: t("configuration_status"),
  dataIndex: "configurationStatus",
  key: "configurationStatus",
  sorter: true,
  sortOrder:
    sorting.sortBy === "IS_ACTIVE" ? (sorting.sortDir === "ASC" ? "ascend" : "descend") : undefined,
  render: (_: unknown, record: WarehouseUnitRecord) => {
    const isActive = getConfigurationActive(record);

    return (
      <div className="flex min-w-30 items-center gap-2 pt-8">
        <span className={`h-2.5 w-2.5 rounded-full ${isActive ? "bg-green" : "bg-gray6"}`} />
        <span className="text-xs font-medium leading-4.5 text-gray9">
          {getConfigurationLabel(record, t)}
        </span>
      </div>
    );
  },
});

const UnitActionCell = ({
  record,
  t,
  handleEditUnit,
  handleEditUnitPosition,
  setDeleteRecord,
  handleSetReadyToInstall,
  handleInstallUnit,
  readyToInstallPendingId,
}: {
  record: WarehouseUnitRecord;
  t: (key: string) => string;
  handleEditUnit: (record: WarehouseUnitRecord) => void;
  handleEditUnitPosition: (record: WarehouseUnitRecord) => void;
  setDeleteRecord: (record: WarehouseUnitRecord | null) => void;
  handleSetReadyToInstall: (record: WarehouseUnitRecord) => void;
  handleInstallUnit: (record: WarehouseUnitRecord) => void;
  readyToInstallPendingId: WarehouseUnitRecord["id"] | null;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="flex min-w-70 items-start justify-between gap-3 pt-6">
      <CustomButton
        type="primary"
        label={getActionLabel(record, t)}
        loading={readyToInstallPendingId === record.id}
        onClick={
          isReadyToInstallOperation(record)
            ? () => handleInstallUnit(record)
            : () => handleSetReadyToInstall(record)
        }
      />
      <Dropdown
        trigger={["click"]}
        placement="bottomRight"
        open={isMenuOpen}
        onOpenChange={setIsMenuOpen}
        dropdownRender={() => (
          <UnitActionMenu
            t={t}
            onEdit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleEditUnit(record);
              setIsMenuOpen(false);
            }}
            onEditPosition={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleEditUnitPosition(record);
              setIsMenuOpen(false);
            }}
            onDelete={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setDeleteRecord(record);
              setIsMenuOpen(false);
            }}
          />
        )}
      >
        <button
          type="button"
          className="mt-1 inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-primary"
          aria-label={t("more_actions")}
          onClick={(event) => event.stopPropagation()}
        >
          <HiOutlineDotsVertical className="h-5 w-5" />
        </button>
      </Dropdown>
    </div>
  );
};

const UnitActionMenu = ({
  onEdit,
  onEditPosition,
  onDelete,
  t,
}: {
  onEdit: (event: MouseEvent<HTMLButtonElement>) => void;
  onEditPosition: (event: MouseEvent<HTMLButtonElement>) => void;
  onDelete: (event: MouseEvent<HTMLButtonElement>) => void;
  t: (key: string) => string;
}) => (
  <div className="all-units-action-menu min-w-55 overflow-hidden rounded-[3px] bg-white py-1 shadow-[0_8px_32px_rgba(16,24,40,0.16)]">
    <button
      type="button"
      className="flex h-[42px] w-full items-center px-5 text-left text-sm font-medium text-gray10 hover:bg-light2"
      onClick={onEdit}
    >
      {t("edit")}
    </button>
    <button
      type="button"
      className="flex h-[42px] w-full items-center px-5 text-left text-sm font-medium text-gray10 hover:bg-light2"
      onClick={onEditPosition}
    >
      {t("edit_position")}
    </button>
    <button
      type="button"
      className="flex h-[42px] w-full items-center px-5 text-left text-sm font-medium text-red hover:bg-red-soft"
      onClick={onDelete}
    >
      {t("delete")}
    </button>
  </div>
);

const AllUnits = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { selectedDistributor } = useSelectDistributor();
  const [activeTab, setActiveTab] = useState<WarehouseUnitType>("DVC");
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorting, setSorting] = useState<WarehouseUnitSorting>(DEFAULT_SORTING);
  const [deleteRecord, setDeleteRecord] = useState<WarehouseUnitRecord | null>(null);
  const [installRecord, setInstallRecord] = useState<WarehouseUnitRecord | null>(null);
  const [editRecord, setEditRecord] = useState<WarehouseUnitRecord | null>(null);
  const [editPositionRecord, setEditPositionRecord] = useState<WarehouseUnitRecord | null>(null);
  const [isAddUnitOpen, setIsAddUnitOpen] = useState(false);
  const distributorId = getOptionalDistributorId(selectedDistributor);
  const { mutateAsync: deleteWarehouseUnit, isPending: isDeleteLoading } =
    useMutationDeleteWarehouseUnit();
  const { data: operationTypeLookupData } = useQueryGetWarehouseUnitOperationTypeLookup();
  const {
    mutate: setReadyToInstall,
    isPending: isSettingReadyToInstall,
    variables: readyToInstallVariables,
  } = useMutationUpdateWarehouseUnitOperationType();

  const params: WarehouseUnitListParams = useMemo(
    () => ({
      page: pagination.page,
      size: pagination.size,
      unitType: activeTab,
      sortBy: sorting.sortBy ?? "ID",
      sort: sorting.sortDir ?? "DESC",
      ...(distributorId ? { idDistributor: distributorId } : {}),
    }),
    [activeTab, distributorId, pagination.page, pagination.size, sorting.sortBy, sorting.sortDir]
  );

  const { data, isLoading } = useQueryGetWarehouseUnitsList(params, {
    enabled: true,
  });

  const response = data?.data?.response;
  const records: WarehouseUnitRecord[] =
    response?.units ?? response?.items ?? response?.data ?? response?.content ?? [];
  const totalCount =
    response?.totalItems ?? response?.totalElements ?? response?.total ?? records.length;

  const readyToInstallOperationTypeId = useMemo(() => {
    const operationTypes = operationTypeLookupData?.data?.response ?? [];
    return operationTypes.find(isReadyToInstallOperationTypeEntry)?.id ?? null;
  }, [operationTypeLookupData]);

  const handleEditUnit = useCallback((record: WarehouseUnitRecord) => {
    setEditRecord(record);
  }, []);

  const handleEditUnitPosition = useCallback((record: WarehouseUnitRecord) => {
    setEditPositionRecord(record);
  }, []);

  const handleInstallUnit = useCallback((record: WarehouseUnitRecord) => {
    setInstallRecord(record);
  }, []);

  const handleDeleteUnit = async () => {
    if (!deleteRecord?.id) return;

    try {
      await deleteWarehouseUnit(deleteRecord.id);
      message.success(t("unit_deleted_successfully"));
      setDeleteRecord(null);
    } catch {
      message.error(t("failed_to_delete_unit"));
    }
  };

  const handleSetReadyToInstall = useCallback(
    (record: WarehouseUnitRecord) => {
      if (!readyToInstallOperationTypeId) {
        message.error(t("failed_to_set_unit_as_ready_to_install"));
        return;
      }

      setReadyToInstall(
        { id: record.id, operationTypeId: readyToInstallOperationTypeId },
        {
          onSuccess: () => message.success(t("unit_set_as_ready_to_install_successfully")),
          onError: () => message.error(t("failed_to_set_unit_as_ready_to_install")),
        }
      );
    },
    [message, readyToInstallOperationTypeId, setReadyToInstall, t]
  );

  const onTableChange: TableProps<WarehouseUnitRecord>["onChange"] = (
    _pagination,
    _filters,
    sorter,
    extra
  ) => {
    if (extra.action === "filter") return;

    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const order = normalizedSorter?.order;
    const dataIndex = normalizedSorter?.field || normalizedSorter?.columnKey;

    if (!order || !dataIndex) {
      setSorting(DEFAULT_SORTING);
      return;
    }

    const sortBy = warehouseUnitDataIndexToColumnId[String(dataIndex)];

    if (!sortBy) {
      setSorting(DEFAULT_SORTING);
      return;
    }

    setSorting({
      sortBy,
      sortDir: order === "ascend" ? "ASC" : "DESC",
    });
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const readyToInstallPendingId = isSettingReadyToInstall
    ? (readyToInstallVariables?.id ?? null)
    : null;

  const columns: WarehouseUnitColumns = useMemo(() => {
    const baseColumns = getBaseColumns({
      t,
      handleEditUnit,
      handleEditUnitPosition,
      setDeleteRecord,
      handleSetReadyToInstall,
      handleInstallUnit,
      readyToInstallPendingId,
      serialOnly: activeTab === "FUESNSR",
      sorting,
    });
    const [unitColumn, ...sharedColumns] = baseColumns;
    const configurationStatusColumn = getConfigurationStatusColumn({ t, sorting });

    if (!unitColumn) {
      return [];
    }

    if (activeTab === "FUESNSR") {
      return [unitColumn, configurationStatusColumn, ...sharedColumns];
    }

    return [
      unitColumn,
      {
        title: `${t("phone_number")}`,
        dataIndex: "phoneNumber",
        key: "phoneNumber",
        sorter: true,
        sortOrder:
          sorting.sortBy === "PHONE_NUMBER"
            ? sorting.sortDir === "ASC"
              ? "ascend"
              : "descend"
            : null,
        render: (value: WarehouseUnitRecord["phoneNumber"]) => (
          <div className="min-w-35 pt-8 text-sm text-gray9">{value || "--"}</div>
        ),
      },
      {
        ...configurationStatusColumn,
      },
      ...sharedColumns,
    ];
  }, [
    activeTab,
    handleEditUnit,
    handleEditUnitPosition,
    handleSetReadyToInstall,
    handleInstallUnit,
    readyToInstallPendingId,
    sorting,
    t,
  ]);

  const tabItems = UNIT_TABS.map((tab) => ({
    key: tab.key,
    label: t(tab.labelKey),
    children: (
      <CustomTable<WarehouseUnitRecord>
        customClass="all-units-table"
        columns={columns}
        data={records}
        rowKey={(record) => record.id}
        pageSize={pagination.size}
        currentPage={pagination.page}
        totalCount={totalCount}
        handlePagination={(page) => setPagination((prev) => ({ ...prev, page }))}
        handlePageSizeChange={(size) => setPagination({ page: 0, size: Number(size) })}
        onChange={onTableChange}
        showSearchInput={false}
        showCustomizeColumns={false}
        showDownload={false}
        isLoading={isLoading}
        locale={{ emptyText: t("no_data") }}
      />
    ),
  }));

  return (
    <section className="all-units-page bg-gray1 pb-8 pl-2">
      <CustomSharedTitleSection
        titleKey="all_units"
        buttonKey="add_unit"
        onClick={() => setIsAddUnitOpen(true)}
        buttonProps={{ className: "!min-w-[106px] !h-10" }}
        titleClassName="!text-2xl !font-bold"
      />

      <div className="overflow-hidden rounded-3xl bg-white">
        <CustomTabs
          className="all-units-tabs"
          items={tabItems}
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key as WarehouseUnitType);
            setPagination(DEFAULT_PAGINATION);
            setSorting(DEFAULT_SORTING);
          }}
          destroyOnHidden={false}
        />
      </div>

      <CustomSharedConfirmationModal
        open={!!deleteRecord}
        onCancel={() => setDeleteRecord(null)}
        onConfirm={handleDeleteUnit}
        title={t("confirm_delete_unit")}
        description={t("the_deletion_operation_is_irreversible")}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        isLoading={isDeleteLoading}
        centered
      />

      <InstallUnitModal
        open={!!installRecord}
        device={
          installRecord
            ? { id: installRecord.id, imei: installRecord.imei, serial: installRecord.serial }
            : null
        }
        onClose={() => setInstallRecord(null)}
      />

      <AddUnitModal
        open={isAddUnitOpen}
        defaultUnitType={activeTab}
        onClose={() => setIsAddUnitOpen(false)}
      />

      <EditUnitModal open={!!editRecord} record={editRecord} onClose={() => setEditRecord(null)} />

      <EditUnitPositionModal
        open={!!editPositionRecord}
        record={editPositionRecord}
        onClose={() => setEditPositionRecord(null)}
      />
    </section>
  );
};

export default AllUnits;
